import { DeviceProfile, getDeviceProfile, View } from "@novorender/api";
import { ObjectDB, type ObjectGroup as SceneObjectGroup, ObjectId } from "@novorender/data-js-api";
import type { Internal } from "@novorender/webgl-api";
import type { Span } from "@opentelemetry/api";
import { getGPUTier } from "detect-gpu";
import { ReadonlyVec3 } from "gl-matrix";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useLazyGetModelTreePermissionsQuery, useLazyGetProjectQuery } from "apis/dataV2/dataV2Api";
import { Permission } from "apis/dataV2/permissions";
import { PipelineConfig, ProjectInfo } from "apis/dataV2/projectTypes";
import { Events, track } from "apis/procore";
import { getModelHomeView } from "apis/procore/bimModelHomeView";
// import { getProcoreMetadataFiles } from "apis/procore/bimFiles"; // TODO: Re-enable when backend implements endpoint
import { getBimModelRevisions } from "apis/procore/bimModelRevision";
import { getProcoreCurrentSceneVersionId } from "apis/procore/bimSceneVersions";
import {
    createBimSignedDownloadUrl,
    createBimSignedFileUrl,
    rewriteSignedUrlForProxy,
} from "apis/procore/bimSignedDownloadUrl";
import { useLazyGetViewpointQuery } from "apis/procore/procoreApi";
import { ProcoreObjectDB } from "apis/procore/procoreObjectDb";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { StorageKey } from "config/storage";
import { explorerGlobalsActions, useExplorerGlobals } from "contexts/explorerGlobals";
import {
    HighlightCollection,
    highlightCollectionsActions,
    useDispatchHighlightCollections,
} from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted } from "contexts/highlighted";
import { GroupStatus, ObjectGroup, objectGroupsActions, useDispatchObjectGroups } from "contexts/objectGroups";
import { newDesignLocalStorageKey } from "features/newDesign/utils";
import { useSetupVersionComparisonPropertyTree } from "features/propertyTree/hooks/useSetupVersionComparisonPropertyTree";
import { Viewpoint } from "features/viewpoints/types";
import { useSetViewpoint, ViewpointError } from "features/viewpoints/useSetViewpoint";
import { useFillGroupIds } from "hooks/useFillGroupIds";
import { useSceneId } from "hooks/useSceneId";
import {
    explorerActions,
    ProjectType,
    selectBackendProvider,
    selectConfig,
    selectInitViewpoint,
    selectInitViewpointId,
    selectProcoreContext,
} from "slices/explorer";
import { UxFlavor } from "slices/explorer/types";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";
import { CustomProperties } from "types/project";
import { AppObjectDB } from "utils/appObjectDb";
import { VecRGBA } from "utils/color";
import { wrapViewForDebugging } from "utils/debugRenderState";
import { mixpanel } from "utils/mixpanel";
import { ObjectIdSet } from "utils/objectIdSet";
import { iterateAsync } from "utils/search";
import { getFromStorage } from "utils/storage";
import { sleep } from "utils/time";

import { renderActions } from "../renderSlice";
import { CadCamera, SceneConfig } from "../types";
import { DEFAULT_CAMERA_PINHOLE_FAR, getDefaultCamera, loadScene, tm2LatLon } from "../utils";
import { useCachedCheckPermissionsQuery } from "./useCachedLazyCheckPermissionsQuery";
import { classifyFailureForTelemetry, InitCanvasError, ViewerLoadTelemetrySession } from "./viewerLoadError";

async function resolveHomeViewpoint(
    params: { companyId: number; projectId: number; sceneId: string; sceneVersionId?: string },
    getViewpointQuery: ReturnType<typeof useLazyGetViewpointQuery>[0],
): Promise<Viewpoint | undefined> {
    try {
        const revisions = await getBimModelRevisions(params);
        const revision =
            revisions.find((r) => r.current_platform_scene_version_id === params.sceneVersionId) ??
            revisions.find((r) => r.current_platform_scene_id === params.sceneId);
        if (!revision?.bim_model_id) {
            return;
        }

        const homeView = await getModelHomeView({
            companyId: params.companyId,
            projectId: params.projectId,
            modelId: revision.bim_model_id,
        });
        if (!homeView?.bim_viewpoint_id) {
            return;
        }

        const resp = await getViewpointQuery({
            companyId: params.companyId,
            projectId: params.projectId,
            viewpointId: homeView.bim_viewpoint_id,
        }).unwrap();
        return resp.data.payload;
    } catch (error) {
        console.warn("Failed to resolve home viewpoint fallback:", error);
        telemetry.trackCaughtException({
            source: "render.useHandleInit.resolveHomeViewpoint",
            category: "init",
            error,
        });
    }
}

type SceneDataForInit = Omit<SceneConfig, "db" | "url" | "settings"> & {
    settings: Internal.RenderSettingsExt | undefined;
};

export function useHandleInit({ uxFlavor: forcedUxFlavor }: { uxFlavor?: UxFlavor }) {
    const sceneId = useSceneId();
    const config = useAppSelector(selectConfig);
    const backendProvider = useAppSelector(selectBackendProvider);
    const procoreContext = useAppSelector(selectProcoreContext);
    const viewpointId = useAppSelector(selectInitViewpointId);
    const initViewpoint = useAppSelector(selectInitViewpoint);
    const [getViewpointQuery] = useLazyGetViewpointQuery();
    const setViewpoint = useSetViewpoint();
    const setViewpointRef = useRef(setViewpoint);
    setViewpointRef.current = setViewpoint;
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchObjectGroups = useDispatchObjectGroups();
    const {
        state: { canvas, internalEventEmitter },
        dispatch: dispatchGlobals,
    } = useExplorerGlobals();
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const [getProject] = useLazyGetProjectQuery();
    const [getModelTreePermissions] = useLazyGetModelTreePermissionsQuery();
    const checkPermissions = useCachedCheckPermissionsQuery();
    const fillGroupIds = useFillGroupIds();
    const setupVersionComparisonPropertyTree = useSetupVersionComparisonPropertyTree();

    const initialized = useRef(false);

    useEffect(() => {
        initView();

        async function initView() {
            if (initialized.current || !canvas) {
                return;
            }

            initialized.current = true;

            telemetry.setGlobalAttributes({
                "scene.id": sceneId,
            });

            const session = new ViewerLoadTelemetrySession(sceneId);
            const telemetrySpan = session.span;
            dispatch(renderActions.setSceneStatus({ status: AsyncStatus.Loading }));

            const detectedTier = await runPhase(telemetrySpan, "gpu_detection", () => loadDeviceTier());
            telemetrySpan?.setAttribute("device.gpu_tier", detectedTier.tier);
            telemetrySpan?.setAttribute("device.is_mobile", detectedTier.isMobile);
            if (detectedTier.gpuName !== undefined) {
                telemetrySpan?.setAttribute("device.gpu_name", detectedTier.gpuName);
            }
            const storedDeviceProfile = getStoredDeviceProfile();
            const deviceProfile = storedDeviceProfile
                ? { ...storedDeviceProfile, isMobile: detectedTier.isMobile }
                : {
                      ...detectedTier,
                      ...(detectedTier.tier >= 0
                          ? getDeviceProfile(detectedTier.tier as DeviceProfile["tier"])
                          : getDeviceProfile(0)),
                      autofreezeEnabled: true,
                  };

            const uxFlavor = forcedUxFlavor ?? getInitUxFlavor(config.uxFlavor);
            const isProcoreDesign =
                uxFlavor === UxFlavor.Procore ||
                uxFlavor === UxFlavor.ProcoreAndroid ||
                uxFlavor === UxFlavor.ProcoreIOS;
            // viewDeviceProfile has only settings relevant for View (not to the bigger app)
            const { autofreezeEnabled = true, ...viewDeviceProfile } = deviceProfile;

            // Procore signed URLs expire after ~24h. The SDK's `urlBuilder` is
            // invoked once per file fetch; we make it async so it can resolve a
            // fresh signed URL (deduped + cached by `createBimSignedFileUrl`)
            // every time, including from inside long-lived web workers.
            //
            // `sceneVersionId` is resolved a few statements below (it can require
            // a network call) so we capture it via a closure variable that's set
            // before `view.loadScene()` triggers any FAS fetch. The view is
            // created lazily; the urlBuilder is not called until `loadScene`.
            let procoreSceneVersionId: string | undefined;

            try {
                const view = await runPhase(telemetrySpan, "view_create", () =>
                    createView(canvas, {
                        deviceProfile: viewDeviceProfile,
                        showWatermark: !isProcoreDesign,
                        urlBuilder:
                            backendProvider === "procore" && procoreContext
                                ? async (_baseUrl, filePath) => {
                                      if (!procoreSceneVersionId) {
                                          throw new Error(
                                              "Procore urlBuilder invoked before sceneVersionId was resolved",
                                          );
                                      }
                                      const fresh = await createBimSignedFileUrl(
                                          {
                                              companyId: procoreContext.companyId,
                                              projectId: procoreContext.projectId,
                                              sceneId,
                                              sceneVersionId: procoreSceneVersionId,
                                          },
                                          filePath,
                                      );
                                      // `fresh` may be relative (when the dev proxy is active).
                                      return new URL(fresh, window.location.origin);
                                  }
                                : undefined,
                    }).catch((err) => {
                        console.error("Error creating view:", err);
                        canvas.remove();
                        throw new InitCanvasError(err);
                    }),
                );

                let db: ObjectDB | undefined;
                let sceneData: SceneDataForInit;
                let sceneCamera: CadCamera | undefined = undefined;
                let url: URL;
                let parentSceneId: string;
                let isVersionComparison = false;
                let projectV2: ProjectInfo | undefined = undefined;
                let permissions: Permission[] = [];
                let tmZoneForCalc: string | undefined = undefined;
                let availableAndForbidden: { availableIds: Set<ObjectId>; forbiddenIds: Set<ObjectId> } | undefined =
                    undefined;
                let pipelineConfig: PipelineConfig | undefined;
                let availableResourcePaths: string[] = [];
                let viewpointPromise: Promise<Viewpoint | undefined> | undefined = undefined;
                // Passed to view.loadScene for Procore. Set in the procore branch below.
                let offlineOnly: boolean | undefined = undefined;

                // A constructor-supplied viewpoint takes precedence over any backend-specific
                // viewpoint resolution (procore `viewpointId` fetch or the resolved home view).
                // Works for both backends. Note: this only seeds the *initial* view — the home
                // viewpoint used by `resetView` is left to backend-specific logic below.
                if (initViewpoint) {
                    viewpointPromise = Promise.resolve(initViewpoint);
                }

                const sceneDataLoadPhase = telemetry.startPhase(telemetrySpan, "scene_data_load");
                try {
                    if (backendProvider === "procore") {
                        if (!procoreContext) {
                            throw Object.assign(new Error("Missing Procore context (companyId/projectId)"), {
                                error: "Missing Procore context (companyId/projectId)",
                                statusCode: 400,
                            });
                        }

                        const sceneVersionId =
                            procoreContext.sceneVersionId ??
                            (await getProcoreCurrentSceneVersionId({
                                companyId: procoreContext.companyId,
                                projectId: procoreContext.projectId,
                                sceneId,
                            }));
                        // Make the resolved sceneVersionId visible to the async
                        // urlBuilder closure created at View.create() time.
                        procoreSceneVersionId = sceneVersionId;
                        let signedUrl: string;
                        // When offline, skip /signed_download_urls and synthesize a FAS URL
                        // from the IDs we have. The SDK falls back to OPFS via requestOfflineFile
                        // (signature/expiry are not validated -- pathname + ?file= match is enough).
                        // Requires the scene was previously synced to OPFS while online.
                        // REACT_APP_SIMULATE_OFFLINE is a dev-only override for when localhost
                        // is reachable but the real API isn't (wifi off + loopback up).
                        const simulateOfflineInDev =
                            import.meta.env.DEV && import.meta.env.REACT_APP_SIMULATE_OFFLINE === "true";
                        const isOffline = !navigator.onLine || simulateOfflineInDev;
                        offlineOnly = isOffline;

                        if (isOffline) {
                            signedUrl = `${window.location.origin}/fas/api/v5.1/files/companies/${procoreContext.companyId}/projects/${procoreContext.projectId}/scenes/${sceneId}/versions/${sceneVersionId}`;
                            session.stamp("procore.offline_synthesized_signed_url", true);
                            if (simulateOfflineInDev) {
                                session.stamp("procore.simulated_offline_dev", true);
                            }
                        } else {
                            try {
                                signedUrl = await createBimSignedDownloadUrl({
                                    companyId: procoreContext.companyId,
                                    projectId: procoreContext.projectId,
                                    sceneId,
                                    sceneVersionId,
                                });
                            } catch (e) {
                                // Mark this as a signed-URL failure for telemetry, then rethrow.
                                session.stamp("procore.signed_url_failed", true);
                                throw e;
                            }
                        }
                        // Always fetch the home viewpoint separately and store it for use in home button.
                        // This runs even when `initViewpoint` is set — the constructor viewpoint only
                        // seeds the initial view; `resetView` continues to apply the resolved home view.
                        const homeViewpointPromise = resolveHomeViewpoint(
                            {
                                companyId: procoreContext.companyId,
                                projectId: procoreContext.projectId,
                                sceneId,
                                sceneVersionId,
                            },
                            getViewpointQuery,
                        );

                        if (!initViewpoint) {
                            if (viewpointId) {
                                viewpointPromise = getViewpointQuery({
                                    companyId: procoreContext.companyId,
                                    projectId: procoreContext.projectId,
                                    viewpointId: viewpointId,
                                })
                                    .unwrap()
                                    .then((resp) => resp.data.payload);
                            } else {
                                viewpointPromise = homeViewpointPromise;
                            }
                        }

                        // Store home viewpoint for home button to use
                        homeViewpointPromise
                            .then((homeVp) => {
                                dispatch(renderActions.setHomeViewpoint(homeVp));
                            })
                            .catch((e) => {
                                console.warn("Failed to store home viewpoint:", e);
                            });

                        // Rewrite signed URL to use proxy if needed (for local development).
                        // The SDK's async urlBuilder (configured above) is what actually
                        // produces per-request URLs at fetch time; this `rewrittenSignedUrl`
                        // is only used as the `baseSceneUrl` placeholder passed to
                        // `view.loadScene(...)`. It does not drive any FAS network call.
                        const rewrittenSignedUrl = rewriteSignedUrlForProxy(signedUrl);

                        // TODO: Re-enable once backend implements the metadata_files endpoint for future metadata/search features.
                        // void getProcoreMetadataFiles({
                        //     companyId: procoreContext.companyId,
                        //     projectId: procoreContext.projectId,
                        //     sceneId,
                        //     sceneVersionId,
                        // }).catch((e) => console.warn("getProcoreMetadataFiles failed:", e));

                        db = new ProcoreObjectDB(
                            procoreContext.companyId,
                            procoreContext.projectId,
                            sceneId,
                            sceneVersionId,
                        );
                        // For Procore backend, create minimal default settings with a default environment
                        sceneData = {
                            title: sceneId,
                            organization: "",
                            tmZone: undefined,
                            projectId: sceneId,
                            objectGroups: [] as SceneObjectGroup[],
                            customProperties: {} as CustomProperties,
                            settings: undefined, // Procore backend doesn't provide settings yet; will use defaults
                        };

                        // NOTE: For Procore, the presigned URL already contains the full path.
                        // We need to strip any path after /versions/<id> since the SDK's urlBuilder
                        // will append file paths via ?file= query parameter

                        // Handle both absolute URLs (production) and relative paths (local dev proxy)
                        if (rewrittenSignedUrl.startsWith("/")) {
                            // Local dev: relative path from proxy rewrite
                            // Construct absolute URL using current origin
                            url = new URL(rewrittenSignedUrl, window.location.origin);
                        } else {
                            // Production: absolute URL
                            url = new URL(rewrittenSignedUrl);
                        }

                        // Ensure the pathname ends at /versions/<sceneVersionId> (no trailing slash)
                        const versionMarker = `/versions/${sceneVersionId}`;
                        const versionIdx = url.pathname.indexOf(versionMarker);
                        if (versionIdx >= 0) {
                            // Trim any extra path after the version ID
                            url.pathname = url.pathname.slice(0, versionIdx + versionMarker.length);
                        }

                        parentSceneId = sceneId; // Not used by SDK when urlBuilder is provided
                    } else {
                        const [sceneConfig, _sceneCamera] = await loadScene(sceneId);
                        const { url: _url, db: _db, ..._sceneData } = sceneConfig;
                        db = _db;
                        sceneData = _sceneData;
                        sceneCamera = _sceneCamera;

                        url = new URL(_url);
                        parentSceneId = url.pathname.replace(/^\//, "").replace(/\/$/, "");
                        isVersionComparison = parentSceneId.includes("/version-comparison/");

                        const [projectV2Res, availableResourcePathsRes] = await Promise.all([
                            getProject({ projectId: sceneId })
                                .unwrap()
                                .catch((e) => {
                                    if (e.status === 401 || e.status === 403) {
                                        throw Object.assign(new Error("Not authorized"), {
                                            error: "Not authorized",
                                            statusCode: e.status,
                                        });
                                    }
                                    throw e;
                                }),
                            getModelTreePermissions(
                                { projectId: isVersionComparison ? (sceneData.projectId ?? sceneId) : sceneId },
                                true,
                            )
                                .unwrap()
                                .then((permissions) => {
                                    return permissions
                                        .filter(
                                            (p) =>
                                                p.path &&
                                                p.permissionIds?.some(
                                                    (p) => p === Permission.SceneView || p === Permission.Scene,
                                                ),
                                        )
                                        .map((p) => p.path as string);
                                }),
                        ]);

                        projectV2 = projectV2Res;
                        availableResourcePaths = availableResourcePathsRes;
                        url.pathname = "";
                    }
                    telemetry.endPhase(sceneDataLoadPhase);
                } catch (e) {
                    telemetry.endPhase(sceneDataLoadPhase, false, e instanceof Error ? e : undefined);
                    throw e;
                }

                telemetry.setGlobalAttributes({
                    "scene.title": sceneData.title,
                    "scene.org": `${sceneData.organization}`,
                });

                const octreeSceneConfig = await runPhase(telemetrySpan, "octree_load", () =>
                    view.loadScene(url, parentSceneId, "index.json", new AbortController().signal, offlineOnly),
                );

                const projectIsV2 = Boolean(projectV2);
                const projectId = sceneData.projectId ?? sceneId;

                if (backendProvider !== "procore") {
                    const [tmZoneForCalcRes, permissionsRes, availableAndForbiddenRes, pipelineConfigRes] =
                        await runPhase(telemetrySpan, "permissions_and_metadata", () =>
                            Promise.all([
                                loadTmZoneForCalc(projectV2, sceneData.tmZone, octreeSceneConfig.center),
                                (async () => {
                                    const checkPermissionsStart = Date.now();
                                    try {
                                        return await checkPermissions({
                                            scope: {
                                                organizationId: sceneData.organization,
                                                projectId,
                                                viewerSceneId:
                                                    sceneId === projectId || isVersionComparison ? undefined : sceneId,
                                            },
                                            permissions: Object.values(Permission),
                                        });
                                    } finally {
                                        telemetrySpan?.setAttribute(
                                            "check_permissions_ms",
                                            Date.now() - checkPermissionsStart,
                                        );
                                    }
                                })(),
                                db && availableResourcePaths.length
                                    ? getAvailableAndForbiddenObjectIds(db, availableResourcePaths)
                                    : undefined,
                                (async () => {
                                    const pipelineConfigFetchStart = Date.now();
                                    try {
                                        const resp = await fetch(await view.buildAssetUrlAsync("config.json"));
                                        if (resp.ok) {
                                            return (await resp.json()) as PipelineConfig;
                                        }
                                        console.warn("Failed to fetch pipeline config", resp.statusText);
                                        return undefined;
                                    } catch (error) {
                                        console.error("Failed to fetch pipeline config", error);
                                        return undefined;
                                    } finally {
                                        telemetry.setSpanAttribute(
                                            telemetrySpan,
                                            "pipeline_config_fetch_ms",
                                            Date.now() - pipelineConfigFetchStart,
                                        );
                                    }
                                })(),
                            ]),
                        );

                    tmZoneForCalc = tmZoneForCalcRes;
                    permissions = permissionsRes;
                    availableAndForbidden = availableAndForbiddenRes;
                    pipelineConfig = pipelineConfigRes;
                }

                mixpanel?.register(
                    {
                        "Scene ID": sceneId,
                        "Scene Title": sceneData.title,
                        "Scene Org": sceneData.organization,
                    },
                    { persistent: false },
                );
                mixpanel?.track_pageview({
                    "Scene ID": sceneId,
                    "Scene Title": sceneData.title,
                    "Scene Org": sceneData.organization,
                });
                telemetry.trackPageview({
                    "scene.id": sceneId,
                    "scene.title": sceneData.title,
                    "scene.org": `${sceneData.organization}`,
                });

                const groups: ObjectGroup[] = sceneData.objectGroups
                    .filter((group) => group.id && group.search)
                    .map((group) => ({
                        name: group.name,
                        id: group.id,
                        grouping: group.grouping ?? "",
                        color: group.color ?? ([1, 0, 0, 1] as VecRGBA),
                        opacity: group.opacity ?? 0,
                        search: group.search ?? [],
                        includeDescendants: group.includeDescendants ?? true,
                        status: group.selected
                            ? GroupStatus.Selected
                            : group.hidden
                              ? GroupStatus.Hidden
                              : group.frozen
                                ? GroupStatus.Frozen
                                : GroupStatus.None,
                        // NOTE(OLA): Pass IDs as undefined to be loaded when group is activated.
                        ids: group.ids ? new ObjectIdSet(group.ids) : (undefined as unknown as ObjectIdSet),
                        // There's a high chance that groups hidden in default view won't be immediately needed,
                        // so freeze them by default
                        promotedToFrozen: autofreezeEnabled && group.hidden,
                    }));

                const getInitialCamera = async () => {
                    return (
                        sceneCamera ??
                        (await getDefaultCamera(
                            db,
                            octreeSceneConfig.aabb
                                ? {
                                      minX: octreeSceneConfig.aabb.min[0],
                                      minY: octreeSceneConfig.aabb.min[1],
                                      minZ: octreeSceneConfig.aabb.min[2],
                                      maxX: octreeSceneConfig.aabb.max[0],
                                      maxY: octreeSceneConfig.aabb.max[1],
                                      maxZ: octreeSceneConfig.aabb.max[2],
                                  }
                                : projectV2?.bounds
                                  ? {
                                        minX: projectV2.bounds[0],
                                        minY: projectV2.bounds[1],
                                        minZ: 0,
                                        maxX: projectV2.bounds[2],
                                        maxY: projectV2.bounds[3],
                                        maxZ: 0,
                                    }
                                  : undefined,
                        )) ?? {
                            position: view.renderState.camera.position,
                            rotation: view.renderState.camera.rotation,
                            fov: view.renderState.camera.fov,
                            kind: view.renderState.camera.kind,
                        }
                    );
                };

                // Ensure frozen groups are loaded before rendering anything to not even put them into memory
                // (some scenes on some devices may crash upon loading because there's too much data)
                // If there's a viewpoint - group loading will be handled by useSetViewpoint hook.
                const getViewpointOrFillGroupIds = async () => {
                    const viewpoint = await viewpointPromise?.catch((error) => {
                        console.error("Error loading viewpoint:", error);
                        // If failed - proceed with the default home view
                        return undefined;
                    });
                    if (!viewpoint) {
                        await fillGroupIds(
                            groups.filter((g) => g.status !== GroupStatus.None),
                            sceneId,
                        );
                    }

                    return viewpoint;
                };

                const [initialCamera, viewpoint] = await runPhase(telemetrySpan, "camera_and_group_fill", () =>
                    Promise.all([getInitialCamera(), getViewpointOrFillGroupIds()]),
                );

                const fallbackProjectV2Info: ProjectInfo = {
                    id: sceneId,
                    name: "",
                    description: "",
                    viewCount: 0,
                    epsg: "",
                    bounds: [0, 0, 0, 0],
                    permissions,
                    created: "",
                    modified: "",
                };

                dispatch(
                    renderActions.initScene({
                        projectType: projectIsV2 ? ProjectType.V2 : ProjectType.V1,
                        projectV2Info: projectV2 ? { ...projectV2, permissions } : fallbackProjectV2Info,
                        pipelineConfig,
                        tmZoneForCalc,
                        sceneData: sceneData as Omit<SceneConfig, "db" | "url">,
                        sceneConfig: octreeSceneConfig,
                        isVersionComparison,
                        initialCamera,
                        deviceProfile,
                        assetsUrl: config.assetsUrl,
                        uxFlavor,
                    }),
                );

                if (isWeakDevice() && getFromStorage(StorageKey.WeakDeviceMessageDismissed) !== "true") {
                    dispatch(explorerActions.setShowWeakDeviceMessage(true));
                }

                dispatch(renderActions.setInitialCamera(initialCamera));

                if (!sceneCamera && octreeSceneConfig.boundingSphere.radius > DEFAULT_CAMERA_PINHOLE_FAR) {
                    // If scene is not configured - camera can be too far by default if project bounds are too big.
                    // Show a snackbar in this case.
                    dispatch(explorerActions.setShowUnusuallyFarObjects(true));
                }

                dispatchObjectGroups(objectGroupsActions.set(groups));
                const primaryHighlightColor =
                    sceneData.customProperties.explorerProjectState?.highlights?.primary?.color ??
                    sceneData.customProperties.highlights?.primary.color;
                const hasLegacyDefaultPrimaryColor =
                    primaryHighlightColor?.[0] === 1 &&
                    primaryHighlightColor?.[1] === 0 &&
                    primaryHighlightColor?.[2] === 0 &&
                    primaryHighlightColor?.[3] === 1;
                const hasLegacyProcorePrimaryColor =
                    primaryHighlightColor?.[0] === 51 / 255 &&
                    primaryHighlightColor?.[1] === 151 / 255 &&
                    primaryHighlightColor?.[2] === 1 &&
                    primaryHighlightColor?.[3] === 1;
                dispatchHighlighted(
                    highlightActions.setColor(
                        isProcoreDesign && (hasLegacyDefaultPrimaryColor || hasLegacyProcorePrimaryColor)
                            ? [24 / 255, 98 / 255, 240 / 255, 1]
                            : (primaryHighlightColor ??
                                  (isProcoreDesign ? [24 / 255, 98 / 255, 240 / 255, 1] : [1, 0, 0, 1])),
                    ),
                );
                dispatchHighlightCollections(
                    highlightCollectionsActions.setColor(
                        HighlightCollection.SecondaryHighlight,
                        sceneData.customProperties.explorerProjectState?.highlights?.secondary?.color ??
                            sceneData.customProperties.highlights?.secondary.color ?? [1, 1, 0, 1],
                    ),
                );

                const resizeObserver = new ResizeObserver((entries) => {
                    for (const entry of entries) {
                        const { width, height } = entry.contentRect;
                        // During orientation changes (especially mobile WebView / Chrome TWA),
                        // contentRect can briefly be 0x0. Feeding that into globals and consumers
                        // causes divide-by-zero and poisons WebGL state.
                        if (width < 1 || height < 1) {
                            continue;
                        }
                        dispatchGlobals(
                            explorerGlobalsActions.update({
                                size: {
                                    width,
                                    height,
                                },
                            }),
                        );
                        internalEventEmitter.emit("appResize", { width, height });
                    }
                });

                resizeObserver.observe(canvas);
                // Start the eviction loop -- scene.sync / scene.delete await a
                // response from it via removeSceneFromEviction. Fire-and-forget
                // (don't await): the SDK's init promise stays pending forever
                // in tabs that don't hold the navigator.locks eviction lock.
                const offlineWorkerState = await view.getOfflineViewState().catch((e) => {
                    console.warn("view.getOfflineViewState():", e);
                    return undefined;
                });
                if (offlineWorkerState) {
                    void offlineWorkerState.initEvictionLoop().catch((e) => {
                        console.warn("offlineWorkerState.initEvictionLoop():", e);
                    });
                }

                dispatchGlobals(
                    explorerGlobalsActions.update({
                        db: new AppObjectDB(db!, availableAndForbidden?.availableIds),
                        view: view,
                        scene: octreeSceneConfig,
                        offlineWorkerState,
                        availableObjectIds: availableAndForbidden?.availableIds,
                        forbiddenObjectIds:
                            availableAndForbidden && new Uint32Array(availableAndForbidden.forbiddenIds).sort(),
                    }),
                );

                if (isProcoreDesign) {
                    dispatch(
                        renderActions.setCameraDefaults({
                            pinhole: { usePointerLock: false },
                            orthographic: {
                                deAcceleration: true,
                                touchRotate: true,
                                topDownSnapToAxis: "north",
                                clipping: { far: 10000 },
                            },
                        }),
                    );

                    // Force generated parametric data enabled to allow snapping (e.g., Point to Point, Coordinates tools)
                    // since Procore UX lacks a user control for this feature.
                    dispatch(renderActions.setGeneratedParametricData({ enabled: true }));

                    // Enable picking semi-transparent (X-Ray) objects and toon outline per object.
                    dispatch(
                        renderActions.setAdvanced({
                            pick: { opacityThreshold: 0.1, xrayPickingEnabled: true },
                            toonOutline: { outlineObjects: true },
                            tonemapping: { exposure: 0.6 },
                        }),
                    );

                    // Keep an explicit Procore init override so scene-loaded environment/color cannot leak through.
                    // BM-10029 default: flat Light (Legacy) background with engine-default IBL.
                    // Honor the user's previously selected environment from local storage so we don't dispatch
                    // setBackground twice (which races the SDK texture loader and produces an AbortError on refresh).
                    const storedEnv = getFromStorage(StorageKey.Environment);
                    dispatch(
                        renderActions.setBackground({
                            url: storedEnv || "",
                            color: [180 / 255, 205 / 255, 245 / 255, 1],
                        }),
                    );

                    // Terrain rendered as background (non-selectable by default).
                    dispatch(renderActions.setTerrain({ asBackground: true }));

                    // Force performance outline on regardless of device tier.
                    dispatch(renderActions.setDeviceProfile({ features: { outline: true } }));

                    // Restore navigation cube state from storage
                    const storedNavigationCube = getFromStorage(StorageKey.NavigationCube);
                    if (storedNavigationCube !== "") {
                        dispatch(renderActions.setNavigationCube({ enabled: storedNavigationCube === "true" }));
                    }
                } else {
                    window.document.title = `${sceneData.title} - Novorender`;
                }

                view.run();

                if (!sceneCamera && db && isVersionComparison) {
                    // Don't await - let it load in the background
                    setupVersionComparisonPropertyTree(db, view, pipelineConfig?.version, backendProvider);
                }

                if (viewpoint) {
                    // For loading initial bookmark we use different approach - useHandleInitialBookmark,
                    // but I think we can speed up initial viewpoint loading by putting loading right into useHandleInit, because
                    // - can load viewpoint data in parallel with scene loading
                    // - viewpoint overrides groups, so no need to load IDs for the irrelevant groups
                    try {
                        // Wait for globals and groups to update.
                        // At the first call setViewpoint might not yet have webviewer and view,
                        // so retry a few times with interval.
                        // Use ref of setViewpoint so it uses the latest state of the globals.
                        // I don't like it personally and I've tried to pass the deps manually,
                        // but then there are deps of deps (e.g. webviewer.db) which are not ready yet as well.
                        // There must be a better way to do this, but this one is the simplest.
                        const maxRetries = 10;
                        for (let i = 0; i < maxRetries; i++) {
                            try {
                                await setViewpointRef.current(viewpoint, "system", { setAsInitial: true });
                                break;
                            } catch (e) {
                                if (e instanceof ViewpointError && !e.ready && i < maxRetries - 1) {
                                    await sleep(20);
                                } else {
                                    throw e;
                                }
                            }
                        }
                    } catch (e) {
                        console.error("Error setting viewpoint:", e);
                        // If failed to load viewpoint groups - init home groups
                        await fillGroupIds(
                            groups.filter((g) => g.status !== GroupStatus.None),
                            sceneId,
                        );
                    }
                }

                await sleep(2000);

                // Track BIM model opened event for Procore backend
                if (backendProvider === "procore") {
                    void track(Events.bimModel.opened);
                }

                dispatch(
                    renderActions.setSceneStatus({
                        status: AsyncStatus.Success,
                        data: undefined,
                    }),
                );
                session.succeed();
            } catch (e) {
                console.warn(e);
                const classification = classifyFailureForTelemetry(e, {
                    t,
                    isOnline: navigator.onLine,
                    canvas,
                });

                session.fail(classification);
                dispatch(
                    renderActions.setSceneStatus({
                        status: AsyncStatus.Error,
                        msg: classification.failure.errorKind,
                        stack: classification.stack,
                    }),
                );
            }
        }
    }, [
        canvas,
        dispatch,
        sceneId,
        dispatchGlobals,
        dispatchObjectGroups,
        dispatchHighlighted,
        dispatchHighlightCollections,
        getProject,
        getModelTreePermissions,
        checkPermissions,
        config,
        fillGroupIds,
        setupVersionComparisonPropertyTree,
        t,
        forcedUxFlavor,
        backendProvider,
        procoreContext,
        internalEventEmitter,
        setViewpoint,
        getViewpointQuery,
        viewpointId,
        initViewpoint,
    ]);
}

export function getStoredDeviceProfile():
    | (DeviceProfile & { debugProfile: true; tier: 0; autofreezeEnabled?: boolean })
    | undefined {
    try {
        const debugProfile =
            new URLSearchParams(window.location.search).get("debugDeviceProfile") ?? localStorage["debugDeviceProfile"];

        if (debugProfile) {
            return { tier: 0, ...JSON.parse(debugProfile), debugProfile: true };
        }
    } catch (e) {
        console.warn(e);
    }
}

export async function loadDeviceTier(): Promise<{
    tier: -1 | DeviceProfile["tier"];
    isMobile: boolean;
    gpuName: string | undefined;
}> {
    try {
        const tiers = [0, 50, 75, 300];
        const tierResult = await getGPUTier({
            mobileTiers: tiers,
            desktopTiers: tiers,
        });
        const isApple = tierResult.device?.includes("apple") || false;
        const { isMobile, ...gpuTier } = tierResult;
        let { tier } = gpuTier;

        // GPU is obscured on apple mobile devices and the result is usually much worse than the actual device's GPU.
        if (isMobile && isApple) {
            tier = Math.max(1, tier);
        }

        // All RTX cards belong in tier 2 or higher, but some such as A3000 are not benchmarked and return tier 1.
        if (gpuTier.gpu && /RTX/gi.test(gpuTier.gpu)) {
            tier = Math.max(2, tier);
        }

        // Intel HD Graphics are given tier 2 for some reason, but belonging in tier 0.
        // " " before HD in regex to not catch UHD
        if (gpuTier.gpu && / HD Graphics /gi.test(gpuTier.gpu)) {
            tier = 0;
        }

        if (gpuTier.gpu && /Quadro/gi.test(gpuTier.gpu)) {
            tier = Math.max(2, tier);
        }

        localStorage.setItem("originalDeviceTier", String(tier));

        return {
            tier: tier as DeviceProfile["tier"],
            isMobile: isMobile ?? false,
            gpuName: gpuTier.gpu ?? undefined,
        };
    } catch (e) {
        console.warn(e);
        telemetry.trackCaughtException({ source: "render.useHandleInit.loadDeviceTier", category: "init", error: e });
        return {
            tier: -1 as -1 | DeviceProfile["tier"],
            isMobile: false,
            gpuName: undefined,
        };
    }
}

async function createView(
    canvas: HTMLCanvasElement,
    options?: {
        deviceProfile?: DeviceProfile;
        showWatermark?: boolean;
        urlBuilder?: (baseUrl: string, filePath: string) => URL | Promise<URL>;
    },
): Promise<View> {
    const deviceProfile = options?.deviceProfile ?? getDeviceProfile(0);
    const view = await View.create(canvas, deviceProfile, undefined, {
        showWatermark: options?.showWatermark,
        webglContextAttributes: { failIfMajorPerformanceCaveat: false },
        urlBuilder: options?.urlBuilder,
    });
    view.controllers.flight.input.mouseMoveSensitivity = 4;
    view.controllers.flight.input.disableWheelOnShift = true;

    // Enable render state debugging if explicitly enabled (disabled by default)
    // Set via console: window.setRenderDebugMode(true) then reload page
    const debugEnabled =
        localStorage.getItem("__RENDER_DEBUG_MODE__") === "true" ||
        (window as unknown as { __RENDER_DEBUG_MODE__?: boolean }).__RENDER_DEBUG_MODE__ === true;
    if (debugEnabled) {
        wrapViewForDebugging(view, true);
    }

    return view;
}

function isWeakDevice() {
    const canvas = document.createElement("canvas");
    try {
        const gl = canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true });
        return !gl;
    } finally {
        canvas.remove();
    }
}

async function loadTmZoneForCalc(
    projectV2: ProjectInfo | undefined,
    tmZoneV1: string | undefined,
    sceneCenter: ReadonlyVec3,
) {
    if (projectV2) {
        if (projectV2.epsg) {
            try {
                // Try to use either .wkt or .proj4
                // Some conversions don't work in WKT, probably because proj4 doesn't support newer WKT versions
                // And some conversions don't work in proj4, because they require downloading additional
                const respWkt = await fetch(`https://epsg.io/${projectV2.epsg}.wkt`);
                if (respWkt.ok) {
                    try {
                        const wkt = await respWkt.text();
                        const converted = tm2LatLon({
                            tmZone: wkt,
                            coords: sceneCenter,
                        });
                        if (!Number.isNaN(converted.latitude) && !Number.isNaN(converted.longitude)) {
                            return wkt;
                        }
                    } catch (ex) {
                        console.warn("Error using WKT string for coordinate conversion, use proj4 instead", ex);
                    }
                } else {
                    console.warn(await respWkt.text());
                }

                // WKT failed, try proj4
                const respProj = await fetch(`https://epsg.io/${projectV2.epsg}.proj4`);
                if (respProj.ok) {
                    return await respProj.text();
                } else {
                    console.warn(await respProj.text());
                }
            } catch (ex) {
                telemetry.trackCaughtException({
                    source: "render.useHandleInit.loadTmZoneForCalc",
                    category: "init",
                    error: ex,
                });
                throw ex;
            }
        }
    } else {
        return tmZoneV1;
    }
}

async function getAvailableAndForbiddenObjectIds(
    db: ObjectDB,
    availableResourcePaths: string[],
): Promise<undefined | { availableIds: Set<ObjectId>; forbiddenIds: Set<ObjectId> }> {
    // Remove child routes, because parent access implies access to children as well
    availableResourcePaths = availableResourcePaths.filter(
        (p1) => !availableResourcePaths.some((p2) => p2.length > p1.length && p2.startsWith(p1)),
    );

    const availableIds = new Set<ObjectId>();
    const descendantMap = new Map<ObjectId, ObjectId[]>();

    // Get all files/folders, their parents and descendants and make a set of IDs that are available.
    // This set can be used later to filter out e.g. search results.
    // NB: I've tried to search all paths at once with a single multivalue search, but it was much slower
    await Promise.all(
        availableResourcePaths.map(async (path) => {
            const [[obj]] = await iterateAsync({
                iterator: db.search({ parentPath: path, descentDepth: 0 }, undefined),
                count: 1,
            });

            if (!obj) {
                return;
            }
            availableIds.add(obj.id);

            await Promise.all([
                path.includes("/") &&
                    iterateAsync({
                        iterator: db.search({ parentPath: path, descentDepth: -100 }, undefined),
                        count: 100,
                    }).then(([parents]) => {
                        for (const parent of parents) {
                            availableIds.add(parent.id);
                        }
                    }),
                db
                    .descendants(obj, undefined)
                    .then((descendants) => {
                        for (const id of descendants) {
                            availableIds.add(id);
                        }
                        descendantMap.set(obj.id, descendants);
                    })
                    .catch((_) => undefined),
            ]);
        }),
    );

    // Fetch all root objects and corresponding descendants - this should cover all the object IDs available in the project -
    // and make a list of forbidden IDs, which is going to be passed to the highlights as the last group.
    // That's the best way (as opposed to using available IDs) of filtering.
    const forbiddenIds = new Set<ObjectId>();
    const [rootObjects] = await iterateAsync({
        iterator: db.search({ parentPath: "", descentDepth: 1 }, undefined),
        count: 500,
    });

    await Promise.all(
        rootObjects.map(async (obj) => {
            if (!availableIds.has(obj.id)) {
                forbiddenIds.add(obj.id);
            }

            const descendants = descendantMap.get(obj.id) ?? (await db.descendants(obj, undefined).catch((_) => []));
            for (const id of descendants) {
                if (!availableIds.has(id)) {
                    forbiddenIds.add(id);
                }
            }
        }),
    );

    if (forbiddenIds.size === 0) {
        return;
    }

    return { availableIds, forbiddenIds };
}

function getInitUxFlavor(configUxFlavor: string | undefined) {
    if (configUxFlavor && Object.values(UxFlavor).includes(configUxFlavor as UxFlavor)) {
        return configUxFlavor as UxFlavor;
    }

    return localStorage.getItem(newDesignLocalStorageKey) === "false" ? UxFlavor.Classic : UxFlavor.New;
}

/** Runs `fn` inside a telemetry phase span, ensuring the span is always ended. */
async function runPhase<T>(parentSpan: Span | null, name: string, fn: () => Promise<T>): Promise<T> {
    const phase = telemetry.startPhase(parentSpan, name);
    try {
        const result = await fn();
        telemetry.endPhase(phase);
        return result;
    } catch (e) {
        telemetry.endPhase(phase, false, e instanceof Error ? e : undefined);
        throw e;
    }
}
